import { useMemo } from "react";

import {
  useCheckFeatureFlag,
  useFeatureFlagStates,
} from "@/shared/domain/featureFlags/hooks/useCheckFeatureFlag";
import { useOrgEventModel } from "@/shared/domain/orgs/hooks";

import {
  browserClientTools,
  clientToolModules,
  getClientTool,
} from "../clientTools";

/**
 * The client tools offered to the signed-in person in the current org, which
 * include only the event tools the org can use:
 * `specs` to send with a thread request, `modules` to list in the tool
 * settings, and `resolve` to look up a tool a thread asks to run.
 *
 * `flagsSettled` is false while the person's flag states load, and until then
 * `resolve` returns undefined for every board tool. A caller that refuses a
 * call when `resolve` returns undefined waits for `flagsSettled`, or it would
 * refuse a board tool released to the person.
 */
export function useReleasedClientTools() {
  const boardsReleased = useCheckFeatureFlag("release-boards");
  const { isPending: flagsLoading } = useFeatureFlagStates();
  const eventModel = useOrgEventModel();
  return useMemo(
    () => ({
      specs: browserClientTools({ boardsReleased, eventModel }),
      modules: clientToolModules({ boardsReleased, eventModel }),
      resolve: (toolName: string) =>
        getClientTool(toolName, { boardsReleased }),
      flagsSettled: !flagsLoading,
    }),
    [boardsReleased, eventModel, flagsLoading],
  );
}
